import { DiscountStrategy, Product, ProductSchema } from "./Product";
import * as z from "zod";
import { SpecialPricePeriod } from "./SpecialPricePeriod";
import { v7 } from "uuid";
import { assert } from "@greeter/assert";

export const BundleIdSchema = z.string();
export type BundleId = z.infer<typeof BundleIdSchema>;

export const BundleLineIdSchema = z.string();
export type BundleLineId = z.infer<typeof BundleLineIdSchema>;

export const BundleLineVariantIdSchema = z.string();
export type BundleLineVariantId = z.infer<typeof BundleLineVariantIdSchema>;

export const BundleLineVariantSchema = z.object({
  id: BundleLineVariantIdSchema,
  name: z.string(),
  /**
   * When withing the limits of a bundle line, and this is set, adds to the overall total
   */
  additionalPrice: z.number(), // Wrapping in an object since this allows for future multicurrency support.
  /**
   * Used when Bundle Line is exceeded and we need to use the sales price
   */
  salesPrice: z.number().default(0),
  productId: z.string(),
  priority: z.number().default(0),
  quantity: z.number().default(0).optional().nullable(),
});
export type BundleLineVariant = z.infer<typeof BundleLineVariantSchema>;

export const BundleLineSchema = z.object({
  id: BundleLineIdSchema,
  name: z.string(),
  quantity: z.number().gte(1).default(1),
  variants: z.array(BundleLineVariantSchema),
  priority: z.number().default(0),
});
export type BundleLine = z.infer<typeof BundleLineSchema>;

// TODO: This should be venue oriented
export const BundleSchema = ProductSchema.extend({
  id: BundleIdSchema,
  isPublic: z.boolean().default(true),
  lines: z.array(BundleLineSchema),
});
export type Bundle = z.infer<typeof BundleSchema>;

export function deepCopyBundleLineVariant(
  bundleLineVariant: BundleLineVariant
): BundleLineVariant {
  return { ...bundleLineVariant };
}
export function deepCopyBundleLine(bundleLine: BundleLine): BundleLine {
  return {
    ...bundleLine,
    variants: bundleLine.variants.map(deepCopyBundleLineVariant),
  };
}
export function deepCopyBundle(bundle: Bundle): Bundle {
  return {
    ...bundle,
    lines: bundle.lines.map(deepCopyBundleLine),
  };
}

export const BundlesSchema = z.array(BundleSchema);
export type Bundles = z.infer<typeof BundlesSchema>;

export function emptyBundle(): Bundle {
  return {
    id: v7(),
    title: "",
    subTitle: "",
    description: "",
    lines: [],
    price: 0,
    coverUrl: "",
    discountStrategy: "Percentage",
    discountPercentage: 0,
    discountFixed: 0,
    priority: 0,
    isPublic: true,
    categories: [],
  };
}

export function indexBundles(bundles: Array<Bundle>): {
  lines: Map<BundleLineId, BundleLine>;
  variants: Map<BundleLineVariantId, BundleLineVariant>;
} {
  if (!bundles) return { lines: new Map(), variants: new Map() };

  const indexLines = new Map<BundleLineId, BundleLine>();
  const indexVariant = new Map<BundleLineVariantId, BundleLineVariant>();

  for (const bundle of bundles.values()) {
    for (const bundleLine of bundle.lines) {
      indexLines.set(bundleLine.id, bundleLine);
      for (const bundleLineVariant of bundleLine.variants) {
        indexVariant.set(bundleLineVariant.id, bundleLineVariant);
      }
    }
  }

  return { lines: indexLines, variants: indexVariant };
}

/**
 * Will summarize lines on bundle or the description.
 *
 * This is to support the old way of doing things and should be removed in the future
 *
 * TODO: Remove when new booking flow has stabilized
 */
export function getDescription(b: Bundle | Product): string {
  if (!("lines" in b)) {
    return b.description;
  }

  const lines = b.lines;

  if (!lines || lines.length === 0) {
    return b.description;
  }

  const description: Array<string> = [];
  for (let i = 0, l = lines.length; i < l; i++) {
    const line = lines[i];
    description.push(`${line.quantity} x ${line.name}`);
  }

  return description.join("\n");
}

export function isBundleDiscounted(b: Bundle): boolean {
  return !!b.discountPercentage;
}

export type BundlePrice =
  | { type: "regular"; price: number }
  | { type: "special"; price: number }
  | { type: "discounted"; price: number; discountedPrice: number };

export type GetBundlePriceOptions = {
  specialPricePeriods?: Array<SpecialPricePeriod>;
  forDate?: Date;
};

type Pricable = {
  price: number;
  discountStrategy: DiscountStrategy;
  discountFixed: number;
  discountPercentage: number;
};
type Discountable = {
  price: number;
  discountStrategy: DiscountStrategy;
  discountFixed: number;
  discountPercentage: number;
};

/**
 * Tries to get the discounted price if any. If not returns regular price.
 * TODO: Should this be a WASM module to align backend and frontend calculations?
 */
export function getDiscountedPrice<T extends Discountable>(p: T): number {
  if (p.discountStrategy === "Percentage") {
    return Number(
      Math.max(0, p.price - p.price * (p.discountPercentage / 100)).toFixed(2)
    );
  }

  return Number(Math.max(0, p.price - p.discountFixed).toFixed(2)) ?? 0;
}

/**
 * Function to calculate the actual price of a bundle;
 *
 * TODO: Maybe this should be a wasm module or something to run on both the backend and frontend. So that we get consistent floating point calculations
 */
export function getPrice<T extends Pricable>(
  b: T,
  opts?: GetBundlePriceOptions
): BundlePrice {
  opts ??= {};

  if (opts.specialPricePeriods) {
    opts.forDate ??= new Date();
    const { forDate, specialPricePeriods } = opts;
    for (let i = 0, l = specialPricePeriods.length; i < l; i++) {
      const specialPricePeriod = specialPricePeriods[i];

      if (
        specialPricePeriod.period.from <= forDate &&
        specialPricePeriod.period.to >= forDate
      ) {
        return { type: "special", price: specialPricePeriod.price };
      }
    }
  }

  if (b.discountPercentage > 0 || b.discountFixed > 0) {
    const discountPrice = getDiscountedPrice({
      price: b.price,
      discountStrategy: b.discountStrategy,
      discountPercentage: b.discountPercentage,
      discountFixed: b.discountFixed,
    });
    return {
      type: "discounted",
      discountedPrice: discountPrice,
      price: b.price,
    };
  }
  return { type: "regular", price: b.price };
}

export function sumAdditionalPriceVariant(v: BundleLineVariant) {
  return v.additionalPrice * (v.quantity ?? 0);
}

export function sumAdditionalPrice(b: Bundle) {
  let result = 0;
  for (const line of b.lines) {
    for (const variant of line.variants) {
      result += sumAdditionalPriceVariant(variant);
    }
  }
  return result;
}

export function unwrapPrice(price: BundlePrice): number {
  if (price.type === "discounted") return price.discountedPrice;
  return price.price;
}

export function getSavings(b: Discountable) {
  return b.price - getDiscountedPrice(b);
}

export function hasDiscounts(b: Discountable) {
  return b.discountFixed > 0 || b.discountPercentage;
}

/**
 * @throws {AssertionError} if discountPercentage is undefined
 */
export function toDiscountable(b: Pricable): Discountable {
  assert(
    b.discountPercentage !== undefined || b.discountFixed !== undefined,
    "requires discountPercentage to be defined"
  );
  return {
    price: b.price,
    discountPercentage: b.discountPercentage,
    discountFixed: b.discountFixed,
    discountStrategy: b.discountStrategy,
  };
}
